/**
 * Judgment Lens Inference
 * =======================
 *
 * Infers a person's characteristic *stance* — the lens through which they tend
 * to see decisions — from the observable pattern of their signals, never from
 * self-report. The point is not to box people in; it is to let a leader
 * assemble a decision from complementary lenses: someone reliably surfacing what
 * could break paired with someone reliably surfacing what could be won.
 *
 * The inference is deliberately transparent (a weighted read of a handful of
 * behavioural features), so a UI can always explain *why* a person reads as a
 * risk-mitigator. It is pure and unit-tested.
 *
 * Design spec: docs/9-exploration/judgment-analytics/DESIGN.md
 */

import { clamp } from './judgmentScoring';
import type { JudgmentLens } from './judgmentTaxonomy';
import { ALL_JUDGMENT_LENSES } from './judgmentTaxonomy';

/**
 * Behavioural features summarising a person's signal history, all normalised to
 * [0, 1] except where noted. These are computed upstream from `decision_signals`
 * (recommendation mix, risk-flag density, confidence, agreement with the crowd).
 */
export interface LensFeatures {
  /** Share of signals recommending 'proceed'. */
  proceedRate: number;
  /** Share of signals recommending 'pause' or 'stop'. */
  cautionRate: number;
  /** Mean number of risk flags per signal, normalised to [0,1] (e.g. flags/5 capped). */
  riskFlagDensity: number;
  /** Mean stated confidence, normalised to [0,1] (e.g. score/10). */
  meanConfidence: number;
  /**
   * Correlation of this person's stances with the group, in [−1, 1]. High =
   * moves with the room; low/negative = forms views apart from it.
   */
  consensusCorrelation: number;
  /** Share of calls where the person meaningfully diverged from the crowd, in [0,1]. */
  contrarianRate: number;
  /**
   * How much the person's confidence tracks reality (calibration), in [0,1].
   * Feeds the 'empirical' lens. Optional; treated as 0.5 when unknown.
   */
  calibration?: number;
}

export interface LensScore {
  lens: JudgmentLens;
  /** Relative strength of this lens for the person, in [0, 1]. */
  weight: number;
}

/**
 * Score every lens for a person from their features. Returns lenses sorted by
 * weight, descending. Weights are normalised to sum to 1 across lenses so they
 * read as a mix ("60% risk-mitigation, 25% systems, …") rather than absolute.
 */
export function inferLensScores(features: LensFeatures): LensScore[] {
  const calibration = features.calibration ?? 0.5;

  // Transparent feature → lens affinities. Each is a raw, non-negative score.
  const raw: Record<JudgmentLens, number> = {
    risk_mitigation: 0.6 * features.cautionRate + 0.4 * features.riskFlagDensity,
    possibility: 0.6 * features.proceedRate + 0.4 * (1 - features.riskFlagDensity),
    // Systems thinkers flag knock-ons: high risk density but *not* dominated by
    // stop calls — they see consequences without simply vetoing.
    systems: 0.5 * features.riskFlagDensity + 0.5 * (1 - Math.abs(features.proceedRate - 0.5) * 2),
    consensus_anchored: 0.7 * clamp(features.consensusCorrelation, 0, 1) + 0.3 * (1 - features.contrarianRate),
    independent: 0.6 * features.contrarianRate + 0.4 * clamp(-features.consensusCorrelation, 0, 1),
    empirical: 0.6 * calibration + 0.4 * (1 - Math.abs(features.meanConfidence - calibration)),
  };

  const total = ALL_JUDGMENT_LENSES.reduce((acc, l) => acc + Math.max(0, raw[l]), 0);
  const scores: LensScore[] = ALL_JUDGMENT_LENSES.map((lens) => ({
    lens,
    weight: total > 0 ? Math.max(0, raw[lens]) / total : 0,
  }));

  return scores.sort((a, b) => b.weight - a.weight);
}

/**
 * The person's dominant lens — the top of `inferLensScores`. Returns null when
 * there is no signal to read (all-zero features).
 */
export function dominantLens(features: LensFeatures): JudgmentLens | null {
  const scores = inferLensScores(features);
  if (scores.length === 0 || scores[0].weight === 0) return null;
  return scores[0].lens;
}
